import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Send, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    contactPreference: '',
    message: '' 
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: false });

  const contactOptions = [
    { value: 'call', label: t('contact.call') },
    { value: 'email', label: t('contact.emailOption') },
    { value: 'whatsapp', label: t('contact.whatsapp') },
    { value: 'signal', label: t('contact.signal') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: false });

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        contact_preference: formData.contactPreference,
        language: language
      });
      setStatus({ loading: false, success: true, error: false });
      setFormData({ name: '', email: '', phone: '', contactPreference: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({ loading: false, success: false, error: true });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-black"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="h-px w-8 bg-[#B8860B]"></div>
            <span className="mx-3 text-[#B8860B] text-xs tracking-[0.2em] uppercase">{t('nav.contact')}</span>
            <div className="h-px w-8 bg-[#B8860B]"></div>
          </div>
          <h2
            data-testid="contact-title"
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-4"
          >
            {t('contact.title')}
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className="order-2 lg:order-1">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.name')}
                  required
                  className="w-full bg-transparent border-b border-white/20 px-0 py-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#B8860B] transition-colors"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact.email')}
                  required
                  className="w-full bg-transparent border-b border-white/20 px-0 py-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#B8860B] transition-colors"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('contact.phone')}
                  required
                  className="w-full bg-transparent border-b border-white/20 px-0 py-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#B8860B] transition-colors"
                  data-testid="contact-phone"
                />
              </div>
              
              {/* Contact Preference */}
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-3">
                  {t('contact.contactPreference')}
                </label>
                <div className="flex flex-wrap gap-3">
                  {contactOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, contactPreference: option.value })}
                      className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                        formData.contactPreference === option.value
                          ? 'bg-[#B8860B] text-black'
                          : 'bg-transparent border border-white/20 text-white/70 hover:border-[#B8860B]/50'
                      }`}
                      data-testid={`contact-pref-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.message')}
                  required
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 px-0 py-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#B8860B] transition-colors resize-none"
                  data-testid="contact-message"
                />
              </div>

              {/* Status Messages */}
              {status.success && (
                <div className="text-green-500 text-sm" data-testid="contact-success">
                  {t('contact.success')}
                </div>
              )}
              {status.error && (
                <div className="text-red-500 text-sm" data-testid="contact-error">
                  {t('contact.error')}
                </div>
              )}

              <button
                type="submit"
                disabled={status.loading || !formData.contactPreference}
                className="w-full bg-transparent border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black py-4 rounded-full text-sm tracking-wide transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Send size={18} />
                <span>{status.loading ? t('contact.sending') : t('contact.send')}</span>
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="order-1 lg:order-2 space-y-8">
            {/* Email Card */}
            <div className="bg-[#0A0A0A] p-8 rounded-xl border border-white/10">
              <h3 className="font-outfit text-xl font-medium text-white mb-4 flex items-center space-x-2">
                <Mail size={24} className="text-[#B8860B]" />
                <span>Email</span>
              </h3>
              <a
                href="mailto:info@alka.website"
                className="text-[#B8860B] hover:underline text-lg"
                data-testid="contact-email-link"
              >
                info@alka.website
              </a>
            </div>

            {/* Info Card */}
            <div className="bg-[#0A0A0A] p-8 rounded-xl border border-white/10">
              <h3 className="font-outfit text-xl font-medium text-white mb-4 flex items-center space-x-2">
                <Phone size={24} className="text-[#B8860B]" />
                <span>{t('contact.contactPreference')}</span>
              </h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                {language === 'de' && 'Wählen Sie im Formular Ihre bevorzugte Kontaktmethode. Wir werden Sie entsprechend kontaktieren.'}
                {language === 'en' && 'Select your preferred contact method in the form. We will reach out to you accordingly.'}
                {language === 'tr' && 'Formda tercih ettiğiniz iletişim yöntemini seçin. Size buna göre ulaşacağız.'}
                {language === 'ru' && 'Выберите предпочтительный способ связи в форме. Мы свяжемся с вами соответствующим образом.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
